import { useMemo } from "react"

const MIN_BAR_HEIGHT = 2
const BAR_GAP = 1

interface BarData {
    id: string
    indexValue: string
    height: number
    value: number | null
}

/**
 * Hook that pre-calculates all cumulative offsets for stacked bar charts.
 * Uses useMemo to compute offsets only when bar data or stackKeys change.
 *
 * @param bars - Array of bar data with id, indexValue, height, and value
 * @param stackKeys - Array of stack keys defining the stacking order
 * @returns Map with keys of format "dataPointKey-barId" and offset values
 */
export const useBarOffsets = (bars: Array<BarData>, stackKeys: Array<string>): Map<string, number> => {
    return useMemo(() => {
        const offsets = new Map<string, number>()
        const cumulativeByDataPoint = new Map<string, number>()

        // Filter out bars with no value (they won't be rendered)
        const barsWithValue = bars.filter((bar) => bar.value && bar.value > 0)

        // Sort bars by dataPoint and stackIndex to ensure correct cumulative calculation
        const sortedBars = [...barsWithValue].sort((a, b) => {
            if (a.indexValue !== b.indexValue) {
                return a.indexValue.localeCompare(b.indexValue)
            }
            return stackKeys.indexOf(a.id) - stackKeys.indexOf(b.id)
        })

        for (const bar of sortedBars) {
            const dataPointKey = bar.indexValue
            const barKey = `${dataPointKey}-${bar.id}`

            const heightDiff = Math.max(0, MIN_BAR_HEIGHT - bar.height)
            const ownOffset = Math.max(0, heightDiff - BAR_GAP)

            const previousCumulative = cumulativeByDataPoint.get(dataPointKey) || 0
            offsets.set(barKey, ownOffset + previousCumulative)
            cumulativeByDataPoint.set(dataPointKey, previousCumulative + ownOffset)
        }

        return offsets
    }, [bars, stackKeys])
}
